import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SettingsGear from './SettingsGear';
import './Navbar.scss';

const Navbar: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoggedIn, setIsLoggedIn] = useState(!!localStorage.getItem('token'));
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    setIsLoggedIn(!!localStorage.getItem('token'));
  }, [location.pathname]);

  useEffect(() => {
    const checkAuth = () => setIsLoggedIn(!!localStorage.getItem('token'));
    window.addEventListener('storage', checkAuth);
    return () => window.removeEventListener('storage', checkAuth);
  }, []);

  const handleLifeMap = (e: React.MouseEvent) => {
    e.preventDefault();
    setMenuOpen(false);
    navigate('/dashboard');
    setTimeout(() => document.getElementById('life-map-section')?.scrollIntoView({ behavior: 'smooth' }), 100);
  };

  const authNavItems: { path: string; labelKey: string; external?: boolean }[] = [
    { path: '/dashboard', labelKey: 'nav.dashboard' },
    { path: '/calendar', labelKey: 'nav.calendar' },
    { path: '/chat', labelKey: 'nav.aiChat' },
    { path: '/dashboard#life-map', labelKey: 'nav.lifeMap' },
    { path: '/subscription', labelKey: 'nav.subscription' },
  ];

  const guestNavItems: { path: string; labelKey: string }[] = [
    { path: '/', labelKey: 'nav.home' },
    { path: '/login', labelKey: 'nav.login' },
    { path: '/register', labelKey: 'nav.register' },
  ];

  const items = isLoggedIn ? authNavItems : guestNavItems;

  return (
    <header className="navbar" role="banner">
      <div className="navbar__container">
        <NavLink to={isLoggedIn ? '/dashboard' : '/'} className="navbar__brand" onClick={() => setMenuOpen(false)}>
          Numerolife AI
        </NavLink>

        <button
          type="button"
          className="navbar__toggle"
          aria-expanded={menuOpen}
          aria-label={menuOpen ? t('nav.closeMenu') : t('nav.openMenu')}
          onClick={() => setMenuOpen(!menuOpen)}
        >
          <span className="navbar__toggle-bar" />
          <span className="navbar__toggle-bar" />
          <span className="navbar__toggle-bar" />
        </button>

        <div className={`navbar__menu ${menuOpen ? 'navbar__menu--open' : ''}`}>
          <ul className="navbar__links">
            <li>
                <NavLink
                  to="/terms-of-service"
                  className={({ isActive }) => `navbar__link ${isActive ? 'navbar__link--active' : ''}`}
                  onClick={() => setMenuOpen(false)}
                >
                  {t('nav.termsOfService')}
                </NavLink>
              </li>
            <li>
                <NavLink
                  to="/terms"
                  className={({ isActive }) => `navbar__link ${isActive ? 'navbar__link--active' : ''}`}
                  onClick={() => setMenuOpen(false)}
                >
                  {t('nav.privacy')}
                </NavLink>
              </li>
            {items.map((item) => (
              <li key={item.path}>
                {item.path === '/dashboard#life-map' ? (
                  <button type="button" className="navbar__link" onClick={handleLifeMap}>
                    {t(item.labelKey)}
                  </button>
                ) : (
                  <NavLink
                    to={item.path}
                    className={({ isActive }) => `navbar__link ${isActive ? 'navbar__link--active' : ''}`}
                    end={item.path === '/' || item.path === '/dashboard'}
                    onClick={() => setMenuOpen(false)}
                  >
                    {t(item.labelKey)}
                  </NavLink>
                )}
              </li>
            ))}
          </ul>
          <div className="navbar__settings">
            <SettingsGear />
          </div>
          {isLoggedIn && (
            <button
              type="button"
              className="navbar__logout"
              onClick={() => {
                localStorage.removeItem('token');
                localStorage.removeItem('user');
                setIsLoggedIn(false);
                setMenuOpen(false);
                navigate('/');
              }}
            >
              {t('nav.logout')}
            </button>
          )}
        </div>
      </div>
    </header>
  );
};

export default Navbar;
